import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SnackBar from "../SnackBar";
import { buscarComboPorCodigo } from "../../services/auth.service";
import { buscarServicoPorTipo } from "../../services/api-planejamento-tributario.service";
import GenericDatePickerReference from "../GenericDatePickerReference";
import styles from "./styles.module.css";

/**
 * Modal para seleção de grupo econômico e criação de prévias
 * As empresas do grupo serão processadas automaticamente pela API
 * @param {boolean} open - Controla se o modal está aberto
 * @param {function} onClose - Callback para fechar o modal
 * @param {function} onConfirm - Callback com dados selecionados (período, tipos de prévia, idGrupoEconomico)
 * @param {string} tipoPrevia - Tipo da prévia (PIS-COFINS, ICMS, INSS)
 * @param {string} titulo - Título do modal (opcional)
 */
const ModalSelecaoEmpresa = ({
  open,
  onClose,
  onConfirm,
  tipoPrevia = "PIS-COFINS",
  titulo,
}) => {
  const [grupos, setGrupos] = useState([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [servicoRelacionado, setServicoRelacionado] = useState(null);
  const [tiposPrevia, setTiposPrevia] = useState([]);
  const [tiposPreviaSelecionados, setTiposPreviaSelecionados] = useState([]);
  const [periodo, setPeriodo] = useState({ dtInicial: null, dtFinal: null });
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  // Gerar título dinamicamente baseado no tipoPrevia
  const getTitulo = () => {
    if (titulo) return titulo;

    const tipoMap = {
      "PIS-COFINS": "PIS/COFINS",
      ICMS: "ICMS",
      INSS: "INSS",
    };

    const tipoDisplay = tipoMap[tipoPrevia] || tipoPrevia;
    return `Nova Prévia ${tipoDisplay}`;
  };

  // Carregar grupos econômicos e tipos de prévia ligados ao serviço
  useEffect(() => {
    const carregarDados = async () => {
      try {
        setLoading(true);

        // Carregar grupos econômicos
        const responseGrupos = await buscarComboPorCodigo("GRUPOECONOMICO");
        const combosFormat = responseGrupos.detalhes.itensCombo
          .filter((item) => item)
          .map((item) => {
            const split = item.split("/|/");
            return {
              id: split[0],
              detalhes: split[1],
            };
          });

        const uniqueCombos = combosFormat.filter(
          (combo, index, self) =>
            index === self.findIndex((t) => t.detalhes === combo.detalhes)
        );

        setGrupos(uniqueCombos);

        // Carregar serviço e tipos de prévia por tipo (uma única chamada)
        try {
          const response = await buscarServicoPorTipo(tipoPrevia);
          const servicoEncontrado = response?.servico || null;
          const tiposArray = response?.tipos || [];

          setServicoRelacionado(servicoEncontrado);

          const tiposFormatados = tiposArray.map((tipo) => ({
            id: tipo.idTipoPrevia,
            descricao: tipo.descricao,
          }));
          
          console.log('✅ [ModalSelecaoEmpresa] Serviço e tipos carregados:', {
            servico: servicoEncontrado,
            tipos: tiposFormatados
          });
          setTiposPrevia(tiposFormatados);
        } catch (errorServico) {
          console.error('❌ [ModalSelecaoEmpresa] Erro ao buscar serviço/tipos de prévia:', errorServico);
          setServicoRelacionado(null);
          setTiposPrevia([]);
          // ✅ NÃO mostrar erro aqui, apenas logar - o campo deve ficar habilitado mesmo sem tipos
          // O usuário pode tentar novamente ou verificar a conexão
        }
      } catch (error) {
        setObjSnackBar({
          type: "error",
          msg: "Erro ao carregar dados iniciais",
        });
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      carregarDados();
    }
  }, [open, tipoPrevia]);

  const handleGrupoChange = (event, value) => {
    setGrupoSelecionado(value);
    setObjSnackBar({ type: "", msg: "" });
  };

  const handleConfirmar = () => {
    if (!grupoSelecionado) {
      setObjSnackBar({
        type: "error",
        msg: "Selecione um grupo econômico",
      });
      return;
    }

    if (!periodo.dtInicial || !periodo.dtFinal) {
      setObjSnackBar({
        type: "error",
        msg: "Preencha o período (mês/ano inicial e final).",
      });
      return;
    }

    if (periodo.dtInicial > periodo.dtFinal) {
      setObjSnackBar({
        type: "error",
        msg: "Data inicial não pode ser maior que a data final.",
      });
      return;
    }

    if (!tiposPreviaSelecionados.length) {
      setObjSnackBar({
        type: "error",
        msg: "Selecione ao menos um tipo de prévia.",
      });
      return;
    }

    // Preparar dados para retorno (simplificado)
    const dadosSelecao = {
      idGrupoEconomico: parseInt(grupoSelecionado.id, 10),
      nomeGrupo: grupoSelecionado.detalhes,
      dtInicial: periodo.dtInicial,
      dtFinal: periodo.dtFinal,
      tiposPrevia: tiposPreviaSelecionados,
      tiposPreviaIds: tiposPreviaSelecionados.map((tipo) => tipo.id),
      servico: servicoRelacionado,
    };

    onConfirm(dadosSelecao);
    handleLimpar();
  };

  const handleLimpar = () => {
    setGrupoSelecionado(null);
    setPeriodo({ dtInicial: null, dtFinal: null });
    setTiposPreviaSelecionados([]);
    setObjSnackBar({ type: "", msg: "" });
  };

  const handleClose = () => {
    handleLimpar();
    onClose();
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <div className={styles.title}>
          <AddIcon sx={{ marginRight: ".5rem" }} />
          {getTitulo()}
        </div>
      </DialogTitle>
      <DialogContent className={styles.content}>
        {/* Seleção de Grupo Econômico */}
        <div className={styles.field}>
          <Autocomplete
            value={grupoSelecionado}
            onChange={handleGrupoChange}
            options={grupos}
            getOptionLabel={(option) => option.detalhes}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            fullWidth
            size="small"
            loading={loading}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Grupo Econômico"
                placeholder={
                  loading
                    ? "Carregando grupos..."
                    : "Selecione um grupo econômico"
                }
              />
            )}
          />
        </div>

        {/* Período da prévia */}
        <div className={styles.periodRow}>
          <GenericDatePickerReference
            label="Mês/Ano Inicial"
            value={periodo.dtInicial}
            onChange={(value) =>
              setPeriodo((prev) => ({ ...prev, dtInicial: value }))
            }
            className={styles.periodField}
          />
          <GenericDatePickerReference
            label="Mês/Ano Final"
            value={periodo.dtFinal}
            onChange={(value) =>
              setPeriodo((prev) => ({ ...prev, dtFinal: value }))
            }
            className={styles.periodField}
          />
        </div>

        {/* Tipos de Prévia */}
        <div className={styles.field}>
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={tiposPrevia}
            value={tiposPreviaSelecionados}
            getOptionLabel={(option) => option.descricao || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setTiposPreviaSelecionados(value)}
            fullWidth
            loading={loading}
            // ✅ CORREÇÃO: Desabilitar APENAS durante loading
            // Mesmo sem tipos disponíveis, o campo deve estar habilitado para mostrar a mensagem
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipos de Prévia"
                placeholder={
                  loading
                    ? "Carregando tipos de prévia..."
                    : !tiposPrevia.length
                    ? "Nenhum tipo de prévia disponível"
                    : "Selecione os tipos de prévia"
                }
                helperText={
                  !loading && !tiposPrevia.length
                    ? "Nenhum tipo de prévia disponível. Verifique a conexão com o servidor."
                    : ""
                }
                error={!loading && !tiposPrevia.length}
              />
            )}
          />
        </div>
      </DialogContent>

      <DialogActions className={styles.containerBtn}>
        <Button
          onClick={handleClose}
          variant="contained"
          className={styles.btnCancelar}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmar}
          variant="contained"
          className={styles.btnSalvar}
          disabled={
            loading ||
            !grupoSelecionado ||
            !periodo.dtInicial ||
            !periodo.dtFinal ||
            !tiposPreviaSelecionados.length
          }
        >
          Criar Prévia
        </Button>
      </DialogActions>
    </Dialog>

    <SnackBar
      objSnackBar={objSnackBar}
      setObjSnackBar={setObjSnackBar}
    />
    </>
  );
};

export default ModalSelecaoEmpresa;
